import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { MQL_LG } from "../../constants";
import { getFocusableElements } from "../../utils/a11y";
import {
  SUBMENU_CLOSE_EVENT_NAME,
  SUBMENU_OPEN_EVENT_NAME,
} from "./global-header.constants";

@customElement("bp-global-header-submenu")
export class GlobalHeaderSubmenu extends LitElement {
  static styles = css`
    :host {
      --global-header-submenu-tx-opened: 0;
      --global-header-submenu-tx-closed: var(--size-5);
      --global-header-submenu-ty-opened: 0;
      --global-header-submenu-ty-closed: 0;

      display: block;
    }

    .submenu-back {
      margin-inline: var(--size-2);
    }

    button {
      border: none;
      background-color: transparent;
      padding: var(--size-1-5) 0;
      margin: 0;
      font: inherit;
      font-weight: var(--font-weight-medium);
      color: inherit;
      cursor: pointer;
      appearance: none;
      text-align: inherit;
      text-decoration: none;
      display: flex;
      align-items: center;
      position: sticky;
      inset-block-start: var(--size-5);
      inline-size: 100%;
    }

    button:focus-visible {
      outline: var(--a11y-outline);
    }

    button:focus:not(:focus-visible) {
      outline: none;
    }

    button:active {
      opacity: var(--button-opacity-active);
    }

    @media (hover: hover) {
      button:hover {
        opacity: var(--button-opacity-hover);
      }
    }

    ::slotted(ul) {
      white-space: nowrap;
    }

    ::slotted(div[slot="footer"]) {
      margin-block-start: var(--size-2);
      margin-inline: var(--size-3);
    }

    @media (min-width: 1024px) {
      :host {
        --global-header-submenu-tx-opened: 0;
        --global-header-submenu-tx-closed: 0;
        --global-header-submenu-ty-opened: var(--size-5);
        --global-header-submenu-ty-closed: calc(
          var(--size-5) + var(--size-0-5)
        );
      }

      ::slotted(div[slot="footer"]) {
        margin-inline: 0;
      }

      .submenu {
        min-width: var(--size-25);
        background-color: var(--color-white);
        border: 1px solid var(--color-neutral-10);
        border-radius: var(--radius-md);
        padding: var(--size-1);
        box-shadow: var(--shadow-lg);
        position: relative;
        z-index: 0;
      }
    }
  `;

  @property({ attribute: "label-back" })
  labelBack: string = "";

  #desktopMql = MQL_LG;

  @state()
  useDesktopLayout = this.#desktopMql.matches;

  get $items() {
    return getFocusableElements(this);
  }

  constructor() {
    super();

    this.#desktopMql.addEventListener(
      "change",
      this.handleDesktopMediaQueryChange,
    );
  }

  close() {
    this.dispatchEvent(
      new CustomEvent(SUBMENU_CLOSE_EVENT_NAME, {
        composed: true,
        detail: {
          id: this.id,
        },
      }),
    );
  }

  focusFirstItem() {
    this.$items?.[0].focus();
  }

  handleDesktopMediaQueryChange = ({ matches }: MediaQueryListEvent) => {
    this.useDesktopLayout = matches;
  };

  open() {
    this.dispatchEvent(
      new CustomEvent(SUBMENU_OPEN_EVENT_NAME, {
        composed: true,
        detail: {
          id: this.id,
        },
      }),
    );
  }

  render() {
    return html`
      <div class="submenu">
        ${!this.useDesktopLayout && this.labelBack
          ? html`
              <div class="submenu-back">
                <button @click="${this.close}" aria-label="${this.labelBack}">
                  <bp-icon icon="angle-left" size="lg"></bp-icon>
                </button>
              </div>
            `
          : nothing}

        <nav>
          <slot></slot>
          <slot name="footer"></slot>
        </nav>
      </div>
    `;
  }
}
